import type React from "react"
import { useEffect, useState } from "react"
import { Dropdown, Modal } from "react-bootstrap"
import OTPInput from "react-otp-input"
import closeIcon from "../../assets/close.svg"
import useInputValidation from "../../hooks/useInputValidation"
import { postWithoutCredentials } from "../../services/http/http.service"
import { setAlertMode, setMessage, setShowAlert } from "../../store/Alert/alert.slice"
import { setShowCallToCTA } from "../../store/authentication/authentication.slice"
import { showMessageOnFailure } from "../../store/error.actions"
import store from "../../store/store"
import { useAppDispatch, useAppSelector } from "../../store/storeRequests"
import APIList from "../../utils/constants/APIList"
import { APIResponseCodes } from "../../utils/constants/keys.constants"
import StoreSelectors from "../../utils/constants/StoreSelectors.constants"
import type { SubmitEvent } from "../../utils/constants/Types.constants"
import openURL from "../../utils/helpers/openURL"
import validateDomain from "../../utils/helpers/validateDomain"
import "./modals.css"

interface InterestOption {
  id: string
  value: string
}

const CTACall: React.FunctionComponent<any> = () => {
  /** Interset dropdown values */
  const interestValues: InterestOption[] = [
    { id: "1", value: "Identity Management" },
    { id: "2", value: "Investments" },
    { id: "3", value: "Lending" },
    { id: "4", value: "Reporting and Reconciliation Operations" },
    { id: "5", value: "other" },
  ]

  const dispatch = useAppDispatch()
  const { bindValue, reset } = useInputValidation()
  const userState = useAppSelector(StoreSelectors.USER_STATE)

  const [CTAInputs, setCTAInputs] = useState({
    email: "",
    title: "",
    interests: [] as InterestOption[],
    other_category: "",
    pitch: "",
    otp: "",
    termsAccepted: false,
  })
  const [OTPSent, setOTPSent] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")
  const [OTPResent, setOTPResent] = useState(false)
  const [otpSeconds, setOtpSeconds] = useState(30)
  const [otpMinutes, setOtpMinutes] = useState(0)
  const [resentCount, setResentCount] = useState(1)

  useEffect(() => {
    if (OTPSent) {
      const interval = setInterval(() => {
        if (otpSeconds > 0) {
          setOtpSeconds(otpSeconds - 1)
        }

        if (otpSeconds === 0) {
          if (otpMinutes === 0) {
            clearInterval(interval)
          } else {
            setOtpSeconds(59)
            setOtpMinutes(otpMinutes - 1)
          }
        }
      }, 1000)

      return () => {
        clearInterval(interval)
      }
    }
  }, [OTPSent, otpSeconds, otpMinutes])

  const closeModal = () => {
    dispatch(setShowCallToCTA(false))
    setOTPSent(false)
    setCTAInputs({
      title: "",
      other_category: "",
      email: "",
      interests: [],
      pitch: "",
      otp: "",
      termsAccepted: false,

    })
    setOtpSeconds(0)
    setOtpMinutes(0)
    setErrorMsg("")
    setResentCount(1)
    reset()
  }

  /** Capture input values */
  const setInputValue = (ev: any) => {
    setErrorMsg("")
    if (typeof ev !== "object") {
      setCTAInputs({ ...CTAInputs, otp: ev })
      return
    }
    setCTAInputs({ ...CTAInputs, [ev.target.name]: ev.target.value })
  }

  const handleInterestChange = (interest: InterestOption) => {
    setErrorMsg("")
    const isSelected = CTAInputs.interests.some((item) => item.id === interest.id)
    if (isSelected) {
      setCTAInputs({
        ...CTAInputs,
        interests: CTAInputs.interests.filter((item) => item.id !== interest.id),
      })
    } else {
      setCTAInputs({
        ...CTAInputs,
        interests: [...CTAInputs.interests, interest],
      })
    }
  }

  /** Send OTP */
  const sendOTP = async () => {
    const payload = { email: CTAInputs.email }
    const response = await postWithoutCredentials(APIList.CTA_SEND_OTP, payload)
    if (response.statusCode === APIResponseCodes.SUCCESS) {
      setOtpSeconds(30)
      setOtpMinutes(0)
      setOTPSent(true)
    } else {
      store.dispatch(showMessageOnFailure(response?.message))
    }
    return response
  }

  /** Submit CTA Form */
  const handleFormSubmit = async (ev: SubmitEvent) => {
    ev.preventDefault()

    /** OTP submission along with CTA details*/
    if (OTPSent === true) {
      const payload = {
        other_category: CTAInputs.other_category === "" ? null : CTAInputs.other_category,
        title: CTAInputs.title,
        email: CTAInputs.email,
        category_id: CTAInputs.interests.map((i) => (i.id === "5" ? null : Number(i.id))).filter((id) => id !== null),
        pitch: CTAInputs.pitch,
        otp: CTAInputs.otp,
      }

      const response = await postWithoutCredentials(APIList.CTA_FORM_SAVE, payload)

      if (response.statusCode === APIResponseCodes.SUCCESS) {
        setOTPSent(false)
        dispatch(setShowCallToCTA(false))
        dispatch(setMessage("Thanks for Your feedback."))
        dispatch(setAlertMode(true))
        dispatch(setShowAlert(true))
        closeModal()
        return
      } else {
        store.dispatch(showMessageOnFailure(response?.message))
      }
    }

    /** Form submission */
    const emailValid = validateDomain(CTAInputs.email)

    if (emailValid === false) {
      setErrorMsg("Only organization managed email IDs allowed.")
    }

    if (CTAInputs.interests.length === 0) {
      setErrorMsg("Please select at least one category")
      return
    }

    if (emailValid && OTPSent === false) {
      setErrorMsg("")
      sendOTP()
    }
  }

  /** Resend OTP call */
  const resendOTP = async () => {
    // setOtpT(30)
    const response = await sendOTP()
    if (response.statusCode === APIResponseCodes.SUCCESS) {
      if (resentCount + 1 === 2) {
        setOtpSeconds(30)
        setOtpMinutes(1)
        setResentCount(resentCount + 1)
      } else if (resentCount + 1 === 3) {
        setOtpSeconds(59)
        setOtpMinutes(2)
        setResentCount(resentCount + 1)
      } else if (resentCount + 1 >= 3) {
        setOtpSeconds(30)
        setOtpMinutes(0)
        setResentCount(1)
        return
      }

      setOTPResent(true)
      setTimeout(() => {
        setOTPResent(false)
      }, 5000)
    }
  }

  const shouldShowOrganisation = () => {
    return CTAInputs.interests.some((i) => i.value.toLowerCase() === "other")
  }

  // Handle checkbox change
  const handleCheckboxChange = (event: any) => {
    setCTAInputs((prevState) => ({
      ...prevState,
      termsAccepted: event.target.checked,  // Update the checkbox state
    }));
  };

  return (
    <Modal show={userState.showCallToCTA} dialogClassName="cta-modal-xl" className="mf-modal cta-modal" centered>
      <Modal.Header className="justify-content-center mf-modal-header border-0 position-relative">
        <div className="cursor-pointer mf-cta-modal-close text-center" onClick={closeModal}>
          <img width="20" height="20" src={closeIcon || "/placeholder.svg"} alt="close" />
        </div>
        <Modal.Title className="weight-400 text-sec-color">
          <strong className="w-700">Let's build the future, together</strong>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="login-modal-body border-0">
        {!OTPSent && (
          <form className="cta-form row no-negative-margin" onSubmit={handleFormSubmit}>
            <div className="cta-input-container mb-3 col-12 col-md-6 col-lg-6">
              <label className="mf-input-label font-13 weight-400" htmlFor="name">
                <span> Give your idea a short, descriptive title to the usecase</span>
                <span className="error-text-color">*</span>
              </label>
              <input
                type="text"
                name="title"
                id="name"
                className="w-100 text-primary-color font-16 weight-400 mf-input-field"
                maxLength={50}
                placeholder="Enter The Title"
                minLength={10}
                pattern="[A-Za-z0-9.\s]+"
                required
                onChange={setInputValue}
                {...bindValue("title")}
                value={CTAInputs.title}
              />
              {CTAInputs.title.length === 0 && <p className="mb-0 error-msg weight-500">field is required</p>}
              {CTAInputs.title.length !== 0 && CTAInputs.title.length < 10 && (
                <p className="mb-0 error-msg weight-500">Title can't have less than 10 characters</p>
              )}
              {CTAInputs.title.length > 10 && !CTAInputs.title.match(/^[A-Za-z0-9.\s]+$/) && (
                <p className="mb-0 error-msg weight-500">Title can only contain digits & characters</p>
              )}
            </div>

            <div className="cta-input-container mb-3 col-12 col-md-6 col-lg-6">
              <label className="mf-input-label text-capitalize font-13 weight-400" htmlFor="email">
                email <span className="error-text-color">*</span>
              </label>
              <input
                type="text"
                name="email"
                id="org"
                placeholder="Enter Your Email"
                className="w-100 text-primary-color font-16 weight-400 mf-input-field"
                maxLength={100}
                minLength={10}
                pattern="^[a-zA-Z0-9._%+-]+[a-zA-Z0-9]+@[a-z0-9.-]+\.[a-zA-Z]{2,4}"
                required
                onChange={setInputValue}
                {...bindValue("email")}
                value={CTAInputs.email}
              />
              {CTAInputs.email.length === 0 && <p className="mb-0 error-msg weight-500">field is required</p>}
              {CTAInputs.email.length !== 0 && CTAInputs.email.length < 10 && (
                <p className="mb-0 error-msg weight-500">Email can't have less than 10 characters</p>
              )}
              {CTAInputs.email.length > 10 &&
                !CTAInputs.email.match(/^[a-zA-Z0-9._%+-]+[a-zA-Z0-9]+@[a-z0-9.-]+\.[a-zA-Z]{2,4}/) && (
                  <p className="mb-0 error-msg weight-500">Invalid Email ID</p>
                )}
            </div>

            <div className="cta-input-container mb-3 col-12 col-md-6 col-lg-6">
              <label className="mf-input-label text-capitalize font-13 weight-400" htmlFor="email">
                Category <span className="error-text-color">*</span>
              </label>
              <Dropdown className="cta-interest-dropdown">
                <Dropdown.Toggle id="dropdown-cta" className="text-primary-color font-13 mf-input-field" variant='light'>
                  {CTAInputs.interests.length === 0
                    ? "Please select your Category"
                    // : `${CTAInputs.interests.length} selected`}
                    : (CTAInputs.interests.length > 0 && (
                      <div className="mt-2 d-flex flex-wrap gap-2">
                        {CTAInputs.interests.map((interest, index) => (
                          <div key={interest.id} className="d-flex align-items-center gap-2">
                            {interest.value}
                            {index < CTAInputs.interests.length - 1 && <span>, </span>}
                          </div>
                        ))}
                      </div>
                    ))}
                </Dropdown.Toggle>

                <Dropdown.Menu className="cta-dropdown-menu w-100">
                  {interestValues.map((interest) => (
                    <Dropdown.Item
                      key={interest.id}
                      onClick={() => handleInterestChange(interest)}
                      className="cta-dropdown-item"
                    >
                      <input
                        type="checkbox"
                        checked={CTAInputs.interests.some((i) => i.id === interest.id)}
                        onChange={() => handleInterestChange(interest)}
                        className="me-2"
                      />
                      {interest.value}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            
            <div className="cta-input-container col-md-6 col-lg-6">
              <label className="mf-input-label text-capitalize font-13 weight-400" htmlFor="pitch">
                usecase / problem statement <span className="error-text-color">*</span>
              </label>
              <textarea
                name="pitch"
                id="org"
                placeholder="Enter The Problem Statement"
                className="w-100 text-primary-color font-16 weight-400 mf-input-field mf-textarea-cta"
                maxLength={1000}
                minLength={10}
                required
                onChange={setInputValue}
                {...bindValue("pitch")}
                value={CTAInputs.pitch}
                rows={4}
              // cols={50}
              />
              {CTAInputs.pitch.length === 0 && <p className="mb-0 error-msg weight-500">field is required</p>}
              {CTAInputs.pitch.length !== 0 && CTAInputs.pitch.length < 10 && (
                <p className="mb-0 error-msg weight-500">Minimum 10 characters required</p>
              )}
            </div>

            {shouldShowOrganisation() && (
              <div className="cta-input-container mb-3 col-12 col-md-6 col-lg-6">
                <label className="mf-input-label text-capitalize font-13 weight-400" htmlFor="other_category">
                  Other Category <span className="error-text-color">*</span>
                </label>
                <input
                  type="text"
                  name="other_category"
                  id="other_category"
                  className="w-100 text-primary-color font-16 weight-400 mf-input-field"
                  maxLength={100}
                  placeholder="Enter The Other Category"
                  minLength={3}
                  pattern="[A-Za-z0-9+-.,@&$() ]+"
                  required
                  onChange={setInputValue}
                  {...bindValue("other_category")}
                  value={CTAInputs.other_category}
                />
                {CTAInputs.other_category.length === 0 && (
                  <p className="mb-0 error-msg weight-500">field is required</p>
                )}
                {CTAInputs.other_category.length !== 0 && CTAInputs.other_category.length < 3 && (
                  <p className="mb-0 error-msg weight-500">other category name can't have less than 3 characters</p>
                )}
                {CTAInputs.other_category.length > 3 && !CTAInputs.other_category.match(/^[A-Za-z0-9+-.,@&$() ]+$/) && (
                  <p className="mb-0 error-msg weight-500">
                    other category name can't contain special characters other than +-.,@&$()
                  </p>
                )}
              </div>
            )}

            {errorMsg.length !== 0 && (
              <p className="mb-0 error-text-color weight-500 font-13 mt-2 text-center">{errorMsg}</p>
            )}

            <div className="cta-consent-wrapper">
              <label className="cta-consent-label">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  checked={CTAInputs.termsAccepted}
                  onChange={handleCheckboxChange}
                />

                <span className="login-acknowledgement weight-400 font-14">
                  By clicking on Continue, I agree to the{" "}
                  <span
                    onClick={() => openURL("/terms-of-use", "open")}
                    className="weight-500 cursor-pointer"
                  >
                    T & C
                  </span>
                  ,{" "}
                  <span
                    className="weight-500 cursor-pointer"
                    onClick={() => openURL("/privacy-policy", "open")}
                  >
                    Privacy Policy
                  </span>
                </span>
              </label>
            </div>



            {/* <p className="mb-0 weight-400 login-acknowledgement text-center font-14 mt-3">
              By clicking on 'Continue'{" "}
              <span onClick={() => openURL("/terms-of-use", "open")} className="weight-500 cursor-pointer">
                T & C,{" "}
              </span>
              <span className="weight-500 cursor-pointer" onClick={() => openURL("/privacy-policy", "open")}>
                Privacy Policy
              </span>
            </p> */}
            <div className="col-6 mx-auto">
              <button
                disabled={
                  CTAInputs.title.length === 0 ||
                  CTAInputs.email.length === 0 ||
                  CTAInputs.interests.length === 0 ||
                  CTAInputs.pitch.length === 0 ||
                  CTAInputs.termsAccepted === false
                }
                className="mf-submit-btn text-capitalize primary-bg-color text-white border-0 w-100 mt-4 weight-400"
                type="submit"
              >
                continue
              </button>
            </div>
          </form>
        )}

        {/* otp form */}
        {OTPSent && (
          <form className="cta-form" onSubmit={handleFormSubmit}>
            <p className="mb-0 otp-title font-16 text-center weight-400 text-primary-color">
              Enter the OTP sent to given email id <span className="weight-500 break-word">{CTAInputs.email}</span>
            </p>

            <div className="d-flex align-items-center justify-content-center">
              <OTPInput
                value={CTAInputs.otp}
                onChange={setInputValue}
                numInputs={6}
                shouldAutoFocus={true}
                inputStyle="otp-input mf-input-field mt-3 ps-2 text-center"
                renderSeparator={<span className="mx-2"></span>}
                renderInput={(props) => <input name="otp" id="otpInput" required {...props} />}
              />
            </div>
            <div className="col-6 mx-auto">
              <button
                className="mf-submit-btn text-capitalize primary-bg-color text-white border-0 w-100 mt-5 weight-400"
                type="submit"
              >
                continue
              </button>
              {OTPResent && (
                <p className="mb-2 font-16 weight-500 mt-3 text-center text-success">Resent OTP to your email</p>
              )}
              {otpSeconds !== 0 || otpMinutes !== 0 ? (
                <p className="mb-0 font-13 weight-500 mt-3 text-center">
                  <span className="text-primary-color cursor-pointer resend-link-container-disabled">Resend OTP</span>{" "}
                  in
                  {"  0" + otpMinutes + " : " + `${otpSeconds < 10 ? "0" + otpSeconds : otpSeconds}`} seconds
                </p>
              ) : (
                <></>
              )}
              {otpSeconds === 0 && otpMinutes === 0 && (
                <p className="mb-0 font-13 weight-500 mt-3 resend-link-container text-center">
                  <span className="text-primary-color cursor-pointer" onClick={resendOTP}>
                    Resend OTP
                  </span>
                </p>
              )}
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default CTACall
